// Activation Content Configuration
// This file contains tutorial steps for the user activation experience.
//
// To add, change, or remove content, update the arrays below.
// Assets (videos) are hosted on Cloudflare R2 at:
// https://assets.replocdn.com/user-activation/

import type { TutorialLocation } from "schemas/generated/activation";

export type TutorialAnchor =
  | "design-library"
  | "products"
  | "homepage-chat"
  | null;

export interface TutorialStep {
  id: string;
  title: string;
  body: string;
  videoUrl?: string;
  imageUrl?: string;
  /** Order in which this step appears in the tutorial sequence */
  order: number;
  /** Where this tutorial step is shown */
  tutorialLocation: TutorialLocation;
  /** The anchor ID for tutorial highlighting (homepage steps only) */
  tutorialAnchorId?: TutorialAnchor;
}

// -----------------------------------------------------------------------------
// Tutorial Steps
// -----------------------------------------------------------------------------

export const TUTORIAL_STEPS: TutorialStep[] = [
  {
    id: "tutorial-welcome",
    title: "Manage your global brand library",
    body: "Tailor your theme, components, and business context to personalized every output in Replo.",
    videoUrl: "https://assets.replocdn.com/user-activation/brand_library.mp4",
    order: 1,
    tutorialLocation: "homepage",
    tutorialAnchorId: "design-library",
  },
  {
    id: "tutorial-editor-overview",
    title: "Set up or sync your product data",
    body: "Replo will automatically pull in your product data on every single page.",
    videoUrl:
      "https://assets.replocdn.com/user-activation/reference_your_products.mp4",
    order: 2,
    tutorialLocation: "homepage",
    tutorialAnchorId: "products",
  },
  {
    id: "tutorial-add-components",
    title: "Build your first page",
    body: "Describe any page, upload inspiration, or get started with a custom-branded template.",
    videoUrl: "https://assets.replocdn.com/user-activation/ask_chat.mp4",
    order: 3,
    tutorialLocation: "homepage",
    tutorialAnchorId: "homepage-chat",
  },
  {
    id: "tutorial-ask-chat",
    title: "Ask the Chat",
    body: "Ask for any edits: update copy, add sections, restyle, or modify layout.",
    videoUrl: "https://assets.replocdn.com/user-activation/ask_chat.mp4",
    order: 1,
    tutorialLocation: "editor",
  },
  {
    id: "tutorial-reference-element",
    title: "Reference a Specific Element",
    body: "Target a specific element to edit by selecting it in Edit mode and typing in your request.",
    videoUrl:
      "https://assets.replocdn.com/user-activation/reference_specific_elements.mp4",
    order: 2,
    tutorialLocation: "editor",
  },
  {
    id: "tutorial-fine-tune",
    title: "Fine-tune Manually",
    body: "In Edit mode, manually modify properties like products, styles, actions, and spacing.",
    videoUrl:
      "https://assets.replocdn.com/user-activation/fine_tune_manually.mp4",
    order: 3,
    tutorialLocation: "editor",
  },
];

// -----------------------------------------------------------------------------
// Helper Functions
// -----------------------------------------------------------------------------

export function getTutorialSteps({
  tutorialLocation,
}: {
  tutorialLocation: TutorialLocation;
}): TutorialStep[] {
  return TUTORIAL_STEPS.filter(
    (s) => s.tutorialLocation === tutorialLocation,
  ).sort((a, b) => a.order - b.order);
}
